import { type PDFDocumentProxy } from 'pdfjs-dist';

import { IPDFLinkService } from './IPDFLinkService';

const DEFAULT_LINK_REL = 'noopener noreferrer nofollow';

const LinkTarget = {
  BLANK: 2,
  NONE: 0, // Default value.
  PARENT: 3,
  SELF: 1,
  TOP: 4,
};

export type IPDFViewer = {
  scrollToPage: (params: { pageNumber: number } | number) => void;
};

/**
 * This is used to create links in pdf, either to web pages or pdf pages
 * Some methods are not implemented because they are not used in AnnotationLayer
 */
export class PDFLinkService extends IPDFLinkService {
  private readonly pdfDocument: PDFDocumentProxy;
  private readonly pdfViewer: IPDFViewer;
  private readonly _externalLinkEnabled: boolean;

  constructor({
    pdfDocument,
    pdfViewer,
    externalLinkEnabled,
  }: {
    pdfDocument: PDFDocumentProxy;
    pdfViewer: IPDFViewer;
    externalLinkEnabled: boolean;
  }) {
    super();
    this.pdfDocument = pdfDocument;
    this.pdfViewer = pdfViewer;
    this._externalLinkEnabled = externalLinkEnabled;
  }

  /**
   * @param {string|Array} dest - The named, or explicit, PDF destination.
   */
  goToDestination = async (dest: string | []) => {
    if (!this.pdfDocument) {
      return;
    }
    let explicitDest = null;
    let pageNumber: number | null = null;

    if (typeof dest === 'string') {
      explicitDest = await this.pdfDocument.getDestination(dest);
    } else {
      explicitDest = await dest;
    }
    if (!Array.isArray(explicitDest)) {
      console.error(`goToDestination: "${explicitDest}" is not a valid destination array, for dest="${dest}".`);
      return;
    }
    // Dest array looks like that: <page-ref> </XYZ|/FitXXX> <args..>
    const [destRef] = explicitDest;

    if (destRef && typeof destRef === 'object') {
      pageNumber = this.pdfDocument.cachedPageNumber(destRef);

      if (!pageNumber) {
        // Fetch the page reference if it's not yet available. This could
        // only occur during loading, before all pages have been resolved.
        try {
          pageNumber = (await this.pdfDocument.getPageIndex(destRef)) + 1;
        } catch {
          console.error(`goToDestination: "${destRef}" is not a valid page reference, for dest="${dest}".`);
          return;
        }
      }
    } else if (Number.isInteger(destRef)) {
      pageNumber = destRef + 1;
    }

    if (pageNumber) {
      this.pdfViewer?.scrollToPage({ pageNumber });
    }
  };

  /**
   * Adds various attributes (href, title, target, rel) to hyperlinks.
   */
  addLinkAttributes = (link: HTMLAnchorElement, url: string) => {
    if (!url) {
      throw new Error('A valid "url" parameter must provided.');
    }

    if (!this._externalLinkEnabled) {
      link.removeAttribute('href');
      link.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      return;
    }
    const target = LinkTarget.BLANK;

    link.href = link.title = url;

    let targetStr = ''; // LinkTarget.NONE
    switch (target) {
      case LinkTarget.NONE:
        break;
      case LinkTarget.SELF:
        targetStr = '_self';
        break;
      case LinkTarget.BLANK:
        targetStr = '_blank';
        break;
      case LinkTarget.PARENT:
        targetStr = '_parent';
        break;
      case LinkTarget.TOP:
        targetStr = '_top';
        break;
    }
    link.target = targetStr;

    link.rel = DEFAULT_LINK_REL;
  };

  /**
   * @param dest - The PDF destination object.
   * @returns The hyperlink to the PDF object.
   */
  getDestinationHash = (dest: string | []) => {
    if (typeof dest === 'string') {
      if (dest.length > 0) {
        return this.getAnchorUrl('#' + escape(dest));
      }
    } else if (Array.isArray(dest)) {
      const str = JSON.stringify(dest);

      if (str.length > 0) {
        return this.getAnchorUrl('#' + escape(str));
      }
    }

    return this.getAnchorUrl('');
  };

  /**
   * Prefix the full url on anchor links to make sure that links are resolved
   * relative to the current URL instead of the one defined in <base href>.
   * @param anchor - The anchor hash, including the #.
   * @returns The hyperlink to the PDF object.
   */
  getAnchorUrl = (anchor: string) => anchor;

  /**
   * This method can handle actions like GoBack, NextPage, LastPage, etc.
   * Not necessary in our pdf viewer
   */
  executeNamedAction = () => {
    console.warn('executeNamedAction is not supported');
  };

  executeSetOCGState = () => {
    console.warn('OCG is not supported');
  };

  get pagesCount(): number {
    return this.pdfDocument.numPages;
  }

  set page(_: number) {
    console.warn('setting page is not supported');
  }

  get page(): number {
    console.warn('page is not supported');
    return 0;
  }

  set rotation(_: number) {
    console.warn('rotation is not supported');
  }

  get rotation(): number {
    console.warn('rotation is not supported');
    return 0;
  }

  get isInPresentationMode(): boolean {
    return false;
  }

  set externalLinkEnabled(_: boolean) {
    // Constructed once per document; callers pass `externalLinkEnabled` to the constructor.
  }

  get externalLinkEnabled(): boolean {
    return this._externalLinkEnabled;
  }

  goToPage(val: string | number): void {
    this.pdfViewer.scrollToPage({ pageNumber: Number(val) });
  }

  setHash(): void {
    console.warn('setHash is not supported');
  }
}
